import React from 'react';
import {BaseSettingsForm, SelectField} from '@/components/forms';
import {Form, Col, Row} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {useStoreActions} from 'easy-peasy';
import {useAlert} from '@/components/alerts';
import SearchSelect from '@/components/search_select/SearchSelect';
import useData from '@/utils/useData';
import {buildNotificationURL} from '@/utils/urlBuilder';
import axios from 'axios';
import * as yup from 'yup';
import i18n from 'i18n';

interface NotificationScheduleChoice {
    value: string;
    label: string;
}

interface FormValues {
    title: string;
    schedule: string;
    storyboardId: number;
    active: boolean;
}

interface MyNotificationsSettingsFormProps {
    notificationId?: number;
    userId: number;
    userName: string;
    onDone: () => void;
}

const notificationSchema = yup.object({
    title: yup.string().required(i18n.t('jt_notification_title_required.text')),
    schedule: yup.string().required(i18n.t('jt_notification_schedule_required.text')),
    active: yup.boolean(),
    storyboardId: yup.number().required(i18n.t('jt_notification_storyboard_required.text')),
});

const MyNotificationsSettingsForm: React.FC<MyNotificationsSettingsFormProps> = React.memo((
    {
        notificationId,
        userId,
        userName,
        onDone,
    },
) => {
    const {showSuccess} = useAlert();
    const addOrEditNotification = useStoreActions((actions: any) => actions.myNotifications.addOrEditNotification);
    const setReloadNotifications = useStoreActions((actions: any) => actions.myNotifications.setReload);
    const [notificationsScheduleFieldChoices] = useData<NotificationScheduleChoice[]>(
        window.URLS.notificationsScheduleFieldChoices,
        'notificationsScheduleFieldChoices'
    );

    const initialValues: FormValues = {
        title: '',
        schedule: notificationsScheduleFieldChoices?.[0]?.value || '',
        storyboardId: 0,
        active: true,
    };

    const customSuccessHandling = async (response: any, actionName: string) => {
        // Auto-configure user as recipient for new notifications
        if (!notificationId) {
            try {
                await axios.post(buildNotificationURL('configureNotification', response.data.id), {
                    users: [{label: userName, value: userId}]
                });
            } catch (error) {
                console.error('Failed to configure notification user:', error);
            }
        }

        if (notificationId) {
            showSuccess(i18n.t('jt_notification_updated_successfully.text'));
        } else {
            showSuccess(i18n.t('jt_notification_created_successfully.text'));
        }

        // Trigger reload to refresh the list from server
        setReloadNotifications(true);
        onDone();
    };

    return (
        <BaseSettingsForm
            entityId={notificationId || undefined}
            resourceUrl={window.URLS.myNotificationsResource}
            storeActionPath="myNotifications.addOrEditNotification"
            validationSchema={notificationSchema}
            initialValues={initialValues}
            successMessageKey="jt_save.text"
            customSuccessHandling={customSuccessHandling}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="title" >
                                <Form.Label>
                                    {i18n.t('jt_title.text')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    name="title"
                                    isInvalid={formik.touched.title && !!formik.errors.title}
                                    autoFocus
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.title}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="storyboardId" >
                                <Form.Label>
                                    {i18n.t('jt_select_the_storyboard.text')}
                                </Form.Label>
                                <SearchSelect
                                    optionsUrl={window.URLS.myStoryboardsResource}
                                    formik={formik}
                                    fieldName="storyboardId"
                                    valueName="id"
                                    labelName="storyboardStoryInfo"
                                    isPaginated={true}
                                />
                                <Form.Control.Feedback
                                    type="invalid"
                                    style={{
                                        display: formik.touched.storyboardId && formik.errors.storyboardId ? 'block' : 'none',
                                    }}
                                >
                                    {formik.errors.storyboardId}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="schedule" >
                                <Form.Label>
                                    {i18n.t('jt_schedule.text')}
                                </Form.Label>
                                <SelectField
                                    name="schedule"
                                    value={notificationsScheduleFieldChoices?.find((x) => x.value === formik.values.schedule) || null}
                                    onChange={(value) => formik.setFieldValue('schedule', value?.value || '')}
                                    options={notificationsScheduleFieldChoices || []}
                                    isRequired={true}
                                    error={formik.errors.schedule}
                                    touched={formik.touched.schedule}
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" >
                                <Form.Check
                                    name="active"
                                    label={i18n.t('es_active.text')}
                                    type="switch"
                                    onChange={formik.handleChange}
                                    checked={formik.values.active}
                                    isInvalid={!!formik.errors.active}
                                    feedback={formik.errors.active}
                                    id="notificationActive"
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row className="mt-4">
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="my-notifications-settings-save-btn"
                            >
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});

MyNotificationsSettingsForm.displayName = 'MyNotificationsSettingsForm';

export default MyNotificationsSettingsForm; 